/**
 * <FieldError> — the single source of truth for inline form-field error text.
 *
 * Before this atom, inline field errors rendered at four different type scales
 * across the app (`text-label` / `text-body` / `text-caption` / `text-body-sm`),
 * with inconsistent `role="alert"` and `id` wiring for `aria-describedby`. This
 * component fixes ONE canonical scale — `text-label text-danger` (the dominant
 * existing variant, and the exact scale <FormField> already used) — and always
 * carries `role="alert"` so screen readers announce the error, plus an optional
 * `id` so a control can reference it via `aria-describedby`.
 *
 * It renders nothing when there is no message, so consumers can pass a
 * possibly-undefined message directly:
 *
 *   <FieldError id={errId} message={fieldState.error?.message} />
 *
 * Presentational only — it holds no validation logic. `<FormField>` renders its
 * error through this atom so the DS has a single error-text implementation.
 */
import * as React from "react";
import { cn } from "../../utils";

export interface FieldErrorProps {
  /** The error text to render. When empty/undefined, the component renders nothing. */
  message?: React.ReactNode;
  /** HTML id so a control can reference this error via `aria-describedby`. */
  id?: string;
  /** Consumer className merged after the canonical scale (e.g. layout/alignment). */
  className?: string;
}

export function FieldError({ message, id, className }: FieldErrorProps) {
  if (message === undefined || message === null || message === "") {
    return null;
  }
  return (
    <p id={id} role="alert" className={cn("text-label text-danger", className)}>
      {message}
    </p>
  );
}
